import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { Info, Copy, Trash2, ListOrdered } from "lucide-react";
import { Can } from "@/components/can";
import { FileUploader } from "@/components/common/FileUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/common/RichTextEditor";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Recipe } from "@/types/recipe";
import {
  type RecipeStep,
  makeEmptyStep,
  hydrateStep,
  isValidVideoUrl,
  StepSidebarCard,
  AddStepButton,
  FormSection,
  FormField,
  MediaCard,
} from "@/components/recipes/execution-steps";

interface ExecutionStepsFormProps {
  recipe: Recipe;
  /**
   * `renumbered` maps each kept step's number as stored to its number in
   * `steps`, so the method's stages can follow a step that moved when an
   * earlier one was deleted. Resolve once the steps are saved.
   */
  onSave: (steps: RecipeStep[], renumbered: Map<number, number>) => Promise<void> | void;
  onCancel: () => void;
  isSaving?: boolean;
  /** Opens the method's stages. Offered only while the steps are saved. */
  onEditStages?: () => void;
}

/** The number a stored step goes by: its own `step`, else its place, as the stages read it. */
function storedNumber(raw: RecipeStep, i: number): number {
  return typeof raw.step === "number" && raw.step > 0 ? raw.step : i + 1;
}

export function ExecutionStepsForm({
  recipe,
  onSave,
  onCancel,
  isSaving,
  onEditStages,
}: ExecutionStepsFormProps) {
  const { t } = useLingui();

  const stored = Array.isArray(recipe.instructions) ? (recipe.instructions as RecipeStep[]) : [];
  const initialSteps: RecipeStep[] = stored.length > 0 ? stored.map(hydrateStep) : [makeEmptyStep(0)];

  const [steps, setSteps] = useState<RecipeStep[]>(initialSteps);
  // Beside each step, the number it was stored under — null for a step added
  // here. Kept in step with `steps` through every add, copy and delete.
  const [origins, setOrigins] = useState<(number | null)[]>(() =>
    stored.length > 0 ? stored.map(storedNumber) : [null],
  );
  const [savedSteps, setSavedSteps] = useState(() => JSON.stringify(initialSteps));
  const [selectedIndex, setSelectedIndex] = useState(0);
  const dirty = JSON.stringify(steps) !== savedSteps;

  const selected = steps[selectedIndex];

  // A link we cannot embed is a link that renders nothing in preview and in
  // cook mode, so it is refused here rather than saved and forgotten (T-805).
  const videoUrlError = (videoUrl?: string) => {
    const value = videoUrl?.trim();
    if (!value) return null;
    return isValidVideoUrl(value)
      ? null
      : t`Enter a YouTube or Vimeo link — youtube.com, youtu.be or vimeo.com.`;
  };

  function updateStep(patch: Partial<RecipeStep>) {
    setSteps((prev) =>
      prev.map((s, i) => (i === selectedIndex ? { ...s, ...patch } : s)),
    );
  }

  function addStep() {
    const newStep = makeEmptyStep(steps.length);
    setSteps((prev) => [...prev, newStep]);
    setOrigins((prev) => [...prev, null]);
    setSelectedIndex(steps.length);
  }

  function duplicateStep() {
    const copy = { ...steps[selectedIndex], step: steps.length + 1 };
    setSteps((prev) => [...prev, copy]);
    setOrigins((prev) => [...prev, null]);
    setSelectedIndex(steps.length);
  }

  function deleteStep() {
    if (steps.length === 1) return;
    setSteps((prev) =>
      prev
        .filter((_, i) => i !== selectedIndex)
        .map((s, i) => ({ ...s, step: i + 1 })) as RecipeStep[],
    );
    setOrigins((prev) => prev.filter((_, i) => i !== selectedIndex));
    setSelectedIndex((prev) => Math.min(prev, steps.length - 2));
  }

  function removeMedia(url: string) {
    updateStep({
      mediaUrls: (selected?.mediaUrls ?? []).filter((u) => u !== url),
    });
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Section header */}
      <div className="flex items-center justify-between pl-2">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-semibold text-(--color-text-primary)">
            {t`Execution Steps`}
          </h2>
          <span className="inline-flex w-10 items-center justify-center rounded-full bg-(--color-surface-muted) px-2 py-1 text-sm font-medium text-(--color-text-secondary)">
            {steps.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {onEditStages && (
            <Button
              variant="outline"
              size="sm"
              className="h-10 gap-1.5"
              onClick={onEditStages}
              // The stages point at steps by number; numbers not yet saved
              // would point them at the wrong steps.
              disabled={dirty}
              title={dirty ? t`Save the steps before editing stages` : undefined}
            >
              <ListOrdered className="size-4" />
              {t`Edit stages`}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-10 w-20"
            onClick={onCancel}
          >
            {t`Cancel`}
          </Button>
          <Button
            size="sm"
            className="h-10 w-20 bg-(--color-action) text-(--color-action-fg) hover:bg-(--color-action)/90"
            onClick={() => {
              const firstInvalidIndex = steps.findIndex((step) =>
                !!videoUrlError(step.youtubeUrl),
              );

              if (firstInvalidIndex >= 0) {
                setSelectedIndex(firstInvalidIndex);
                return;
              }

              const saving = steps.map((step) => ({
                ...step,
                youtubeUrl: step.youtubeUrl?.trim() || undefined,
              }));
              const renumbered = new Map<number, number>();
              origins.forEach((origin, i) => {
                if (origin !== null) renumbered.set(origin, i + 1);
              });
              void Promise.resolve(onSave(saving, renumbered)).then(
                () => {
                  // Saved, every step is now stored under its place.
                  setOrigins(steps.map((_, i) => i + 1));
                  setSavedSteps(JSON.stringify(steps));
                },
                // A failed save keeps the old numbers, to follow next time.
                () => {},
              );
            }}
            disabled={isSaving}
          >
            {t`Save`}
          </Button>
        </div>
      </div>

      {/* Two-panel area */}
      <div className="flex gap-3 rounded-[10px] border border-(--color-border) bg-(--color-surface-muted) p-3">
        {/* Left sidebar */}
        <div className="flex w-100 shrink-0 flex-col gap-2.5 self-start rounded-[10px] border border-(--color-border) bg-(--color-surface-content) p-3">
          <div className="px-3 py-3">
            <p className="text-xl font-semibold text-(--color-text-primary)">{t`Steps`}</p>
          </div>

          <div className="flex flex-col gap-2">
            {steps.map((step, idx) => (
              <StepSidebarCard
                key={idx}
                step={step}
                index={idx}
                isSelected={idx === selectedIndex}
                showDragHandle
                onClick={() => setSelectedIndex(idx)}
              />
            ))}
            <AddStepButton onClick={addStep} />
          </div>
        </div>

        {/* Right detail panel */}
        {selected != null && (
          <div className="flex min-w-0 flex-1 flex-col gap-3 rounded-[10px] border border-(--color-border) bg-(--color-surface-content) p-3">
            <StepDetailHeader
              stepIndex={selectedIndex}
              onDelete={deleteStep}
              onDuplicate={duplicateStep}
              canDelete={steps.length > 1}
            />

            <div className="h-px bg-(--color-surface-muted)" />

            <CoreInstructionSection
              step={selected}
              onUpdate={updateStep}
              error={videoUrlError(selected.youtubeUrl)}
            />
            <TimingSection
              step={selected}
              stepIndex={selectedIndex}
              onUpdate={updateStep}
            />
            <TechniqueSection step={selected} onUpdate={updateStep} />
            <WarningsSection
              step={selected}
              stepIndex={selectedIndex}
              onUpdate={updateStep}
            />
            <CriticalControlPointSection
              step={selected}
              stepIndex={selectedIndex}
              onUpdate={updateStep}
            />
            <MediaSection
              step={selected}
              onRemoveMedia={removeMedia}
              onUpdate={updateStep}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Step detail sub-sections ──────────────────────────────────────────────────

function StepDetailHeader({
  stepIndex,
  onDelete,
  onDuplicate,
  canDelete,
}: {
  stepIndex: number;
  onDelete: () => void;
  onDuplicate: () => void;
  canDelete: boolean;
}) {
  const { t } = useLingui();
  return (
    <div className="flex items-start justify-between p-3">
      <span className="inline-flex items-center justify-center self-start rounded-full bg-(--color-surface-muted) px-3 py-2 text-sm font-medium text-(--color-text-secondary)">
        {t`Step`} {stepIndex + 1}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="icon"
          className="size-10 border-(--color-danger)/20 bg-(--color-surface-muted) hover:bg-(--color-danger)/10"
          onClick={onDelete}
          disabled={!canDelete}
          title={t`Delete step`}
        >
          <Trash2 className="size-5 text-(--color-text-secondary)" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-10 gap-2 border-(--color-border)"
          onClick={onDuplicate}
        >
          <Copy className="size-5" />
          {t`Duplicate`}
        </Button>
      </div>
    </div>
  );
}

function CoreInstructionSection({
  step,
  onUpdate,
  error,
}: {
  step: RecipeStep;
  onUpdate: (patch: Partial<RecipeStep>) => void;
  error?: string | null;
}) {
  const { t } = useLingui();
  return (
    <FormSection
      title={t`Core Instruction`}
      description={t`The primary action for this step.`}
    >
      <FormField label={t`Instruction`}>
        <Input
          value={step.instruction ?? ""}
          onChange={(e) => onUpdate({ instruction: e.target.value })}
          placeholder={t`e.g. Brine chicken 30 min, pat dry, season`}
          className="h-11"
        />
      </FormField>
      <FormField label={t`Text`}>
        <RichTextEditor
          value={step.description ?? ""}
          onChange={(description) => onUpdate({ description })}
          placeholder={t`Detailed description of this step...`}
        />
      </FormField>
      <FormField label={t`Video URL`}>
        <Input
          value={step.youtubeUrl ?? ""}
          onChange={(e) => onUpdate({ youtubeUrl: e.target.value })}
          placeholder={t`https://www.youtube.com/watch?v=... or https://vimeo.com/...`}
          className="h-11"
          aria-label={t`Video URL`}
          aria-invalid={!!error}
          aria-describedby={error ? "step-video-url-error" : undefined}
        />
        {/*
          The refusal belongs under the box it is about — it used to sit at the
          foot of the whole section, attached to nothing (T-805).
        */}
        {error && (
          <p id="step-video-url-error" role="alert" className="text-xs text-(--color-danger-text)">
            {error}
          </p>
        )}
      </FormField>
    </FormSection>
  );
}

function TimingSection({
  step,
  stepIndex,
  onUpdate,
}: {
  step: RecipeStep;
  stepIndex: number;
  onUpdate: (patch: Partial<RecipeStep>) => void;
}) {
  const { t } = useLingui();
  return (
    <FormSection
      title={t`Timing & Conditions`}
      description={t`Set timer and temperature requirements for this step.`}
    >
      <div className="flex items-center gap-2.5">
        <Switch
          id={`timer-toggle-${stepIndex}`}
          checked={!!step.timerEnabled}
          onCheckedChange={(checked) =>
            onUpdate({
              timerEnabled: checked,
              timer: checked ? (step.timer ?? 5) : step.timer,
              timerUnit: checked ? (step.timerUnit ?? "min") : step.timerUnit,
            })
          }
        />
        <Label
          htmlFor={`timer-toggle-${stepIndex}`}
          className="cursor-pointer text-sm font-medium text-(--color-text-primary)"
        >
          {t`Requires timer`}
        </Label>
        <Info className="size-5 text-(--color-text-secondary)" />
      </div>

      {step.timerEnabled && (
        <div className="flex gap-3">
          <FormField label={t`Time value`} className="flex-1">
            <Input
              type="number"
              min={0}
              value={step.timer ?? ""}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                onUpdate({ timer: isNaN(n) ? undefined : n });
              }}
              className="h-11"
            />
          </FormField>
          <FormField label={t`Unit`} className="w-35">
            <Select
              value={step.timerUnit ?? "min"}
              onValueChange={(v) =>
                onUpdate({ timerUnit: v as RecipeStep["timerUnit"] })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="min">{t`Min`}</SelectItem>
                <SelectItem value="sec">{t`Sec`}</SelectItem>
                <SelectItem value="hr">{t`Hr`}</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
        </div>
      )}

      <div className="flex gap-3">
        <FormField label={t`Temperature value`} className="flex-1">
          <Input
            value={step.temperature ?? ""}
            onChange={(e) =>
              onUpdate({ temperature: e.target.value || undefined })
            }
            placeholder={t`Enter…`}
            className="h-11"
          />
        </FormField>
        <FormField label={t`Unit`} className="w-35">
          <Select
            value={step.temperatureUnit ?? ""}
            onValueChange={(v) =>
              onUpdate({ temperatureUnit: v as RecipeStep["temperatureUnit"] })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder={t`Select`} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="C">°C</SelectItem>
              <SelectItem value="F">°F</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
      </div>
    </FormSection>
  );
}

function TechniqueSection({
  step,
  onUpdate,
}: {
  step: RecipeStep;
  onUpdate: (patch: Partial<RecipeStep>) => void;
}) {
  const { t } = useLingui();
  return (
    <FormSection
      title={t`Technique & Guidance`}
      description={t`Describe the technique or method to use.`}
    >
      <Textarea
        value={step.techniqueNotes ?? ""}
        onChange={(e) => onUpdate({ techniqueNotes: e.target.value || undefined })}
        placeholder={t`Describe the technique…`}
        className="h-30 resize-none"
      />
    </FormSection>
  );
}

function WarningsSection({
  step,
  stepIndex,
  onUpdate,
}: {
  step: RecipeStep;
  stepIndex: number;
  onUpdate: (patch: Partial<RecipeStep>) => void;
}) {
  const { t } = useLingui();
  return (
    <FormSection
      title={t`Warnings & Constraints`}
      description={t`Add safety warnings or constraints for this step.`}
    >
      <div className="flex items-center gap-2.5">
        <Switch
          id={`warning-toggle-${stepIndex}`}
          checked={!!step.warningEnabled}
          onCheckedChange={(checked) =>
            onUpdate({
              warningEnabled: checked,
              warnings: checked ? (step.warnings ?? "") : step.warnings,
            })
          }
        />
        <Label
          htmlFor={`warning-toggle-${stepIndex}`}
          className="cursor-pointer text-sm font-medium text-(--color-text-primary)"
        >
          {t`Add warning label`}
        </Label>
      </div>

      {step.warningEnabled && (
        <FormField label={t`Warnings / Constraints`}>
          <Textarea
            value={step.warnings ?? ""}
            onChange={(e) => onUpdate({ warnings: e.target.value })}
            placeholder={t`Describe the warning or constraint…`}
            className="h-30 resize-none"
          />
        </FormField>
      )}
    </FormSection>
  );
}

/**
 * The critical-control-point flag (YC-21). Only people who can update recipes
 * see it, and the API refuses the change to anyone else. It is a flag and
 * nothing more: no limit, no corrective action. A step already flagged keeps
 * its flag through every edit here whether or not this switch is shown, since
 * the steps are saved as one list and a step left without it is a step cleared.
 */
function CriticalControlPointSection({
  step,
  stepIndex,
  onUpdate,
}: {
  step: RecipeStep;
  stepIndex: number;
  onUpdate: (patch: Partial<RecipeStep>) => void;
}) {
  const { t } = useLingui();
  return (
    <Can permission="recipe:update">
      <FormSection
        title={t`Critical control point`}
        description={t`Flag this step as critical. Everyone who views the recipe sees a warning on it.`}
      >
        <div className="flex items-center gap-2.5">
          <Switch
            id={`ccp-toggle-${stepIndex}`}
            checked={step.isCcp === true}
            // Cleared by leaving the key off, as the API reads absent as not flagged.
            onCheckedChange={(checked) => onUpdate({ isCcp: checked ? true : undefined })}
          />
          <Label
            htmlFor={`ccp-toggle-${stepIndex}`}
            className="cursor-pointer text-sm font-medium text-(--color-text-primary)"
          >
            {t`Mark as critical control point`}
          </Label>
        </div>
      </FormSection>
    </Can>
  );
}

function MediaSection({
  step,
  onRemoveMedia,
  onUpdate,
}: {
  step: RecipeStep;
  onRemoveMedia: (url: string) => void;
  onUpdate: (patch: Partial<RecipeStep>) => void;
}) {
  const { t } = useLingui();
  const [uploadKey, setUploadKey] = useState(0);
  return (
    <FormSection
      title={t`Media`}
      description={t`Attach images for this step.`}
    >
      {step.mediaUrls?.map((url, i) => (
        <MediaCard key={i} url={url} onRemove={() => onRemoveMedia(url)} />
      ))}
      <FileUploader
        key={uploadKey}
        module="recipe"
        multiple
        onUploadSuccess={(results) => {
          const newUrls = results.map((r) => r.imageUrl);
          onUpdate({ mediaUrls: [...(step.mediaUrls ?? []), ...newUrls] });
          setUploadKey((k) => k + 1);
        }}
      />
    </FormSection>
  );
}
